/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import {parserOptions} from "@insite/client-framework/Common/BasicSelectors";
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { GridContainer, GridItem, Typography } from "@insite/mobius/index";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import { zoneTitle } from "@monsma/XDL/Utilities/sharedStyles";
import parse from "html-react-parser";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    pageType: getCurrentPage(state).type,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface FooterStayConnectedStyles {
    wrapper: InjectableCss;
    container: GridContainerProps;
    titleAndDescriptionGridItem: GridItemProps;
    socialMediaLinksGridItem: GridItemProps;
    title: InjectableCss;
    description: InjectableCss;
}

export const footerStayConnectedStyles: FooterStayConnectedStyles = {
    wrapper: {
        css: css`
            width: 1028px;
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            background: ${getColor("primary.main")};
            border-radius: 6px;
            padding: 30px;
            z-index: ${getProp("theme.zIndex.default")};
            top: -74px;

            ${screen("lt", "md")} {
                width: calc(100% - 34px);
            }

            ${screen("only", "md")} {
                width: calc(100% - 48px);
            }

            ${screen("only", "lg")} {
                width: calc(100% - 34px);
            }

            ${screen("only", "xs")} {
                top: -182.5px;
            }

            ${screen("mixMax", "sm")} {
                top: -132.5px;
            }

            ${screen("mixMax", "md")} {
                top: -108px;
            }
        `,
    },
    container: {
        gap: 0,
        css: css`
            > div {
                align-items: center;
            }
        `,
    },
    titleAndDescriptionGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            flex-direction: column;

            ${screen("gt", "sm")} {
                padding-right: 45px;
            }

            ${screen("lte", "sm")} {
                margin-bottom: 30px !important;
            }
        `,
    },
    socialMediaLinksGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;

            > div > div > div {
                padding: 0;

                ${screen("gte", "md")} {
                    justify-content: flex-end;
                }

                > div {
                    padding: 0;
                    margin-right: 30px;
                    line-height: 0;
                    flex: 0 0 auto;

                    ${screen("lt", "md")} {
                        margin-right: 23px;
                    }

                    ${screen("only", "md")} {
                        margin-right: 20px;
                    }

                    a {
                        width: 76px;
                        height: 76px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        transition: all 0.2s linear;
                        border: 1px solid ${getColor("primary.contrast")};
                        border-radius: 4px;

                        &:hover,
                        &:focus {
                            background: ${getColor("primary.contrast")};
                        }

                        ${screen("lte", "md")} {
                            width: 58px;
                            height: 58px;
                        }
                    }

                    &:last-child {
                        margin-right: 0;
                    }

                    span {
                        color: #fff !important;
                        margin: 0 !important;

                        ${screen("lte", "md")} {
                            &:first-child,
                            svg {
                                width: 24px;
                                height: 24px;
                            }
                        }
                    }
                }
            }
        `,
    },
    title: {
        css: css`
            color: ${getColor("primary.contrast")};
            font-size: 22px;
            line-height: 28px;
            margin-bottom: 12px;
            font-weight: 700;

            ${screen("lte", "sm")} {
                margin-bottom: 15px;
            }
        `,
    },
    description: {
        css: css`
            &&& {
              * {
                font-size: 16px;
                line-height: 24px;
                font-weight: 600;
                margin-bottom: 0;
              }
            }
        `,
    },
};

const styles = footerStayConnectedStyles;

const FooterStayConnectedWidget: FC<Props> = ({ id, pageType, fields }) => {
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";
    const isFooterPage = pageType === "Footer";
    const showZoneTitle = isCmsEditing && isFooterPage;

    if (isItCheckoutPage(pageType)) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} id="stayConnectedWidget">
            <GridContainer {...styles.container}>
                <GridItem {...styles.titleAndDescriptionGridItem}>
                    <Typography as="h2" {...styles.title}>
                        {fields.title}
                    </Typography>
                    <Typography as="h5" {...styles.description}>
                         {parse(fields.description || '', parserOptions)}
                    </Typography>
                </GridItem>
                <GridItem {...styles.socialMediaLinksGridItem}>
                    {showZoneTitle && <Typography {...zoneTitle}>Social Links Zone</Typography>}
                    <Zone contentId={id} zoneName="SocialLinks" />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "Title",
            isRequired: true,
        },
        {
            name: fields.description,
            displayName: "Description",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(FooterStayConnectedWidget),
    definition,
};

export default widgetModule;
